/**
 * Analytics — Sales API Contracts
 *
 * Wire format for SalesController and AnalyticsRevenueController endpoints.
 * Shared between analytics-service (producer) and admin/web/kiosk callers.
 */

import { z } from 'zod';

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const EXPORT_TYPES = ['daily', 'weekly', 'monthly', 'ticket_types', 'payment_methods'] as const;

// ---------------------------------------------------------------------------
// Shared query schemas
// ---------------------------------------------------------------------------

export const SalesDateRangeQuerySchema = z.object({
  startDate: z.string().regex(DATE_REGEX).optional(),
  endDate: z.string().regex(DATE_REGEX).optional(),
});
export type SalesDateRangeQuery = z.infer<typeof SalesDateRangeQuerySchema>;

export const MonthlyQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional(),
});
export type MonthlyQuery = z.infer<typeof MonthlyQuerySchema>;

export const TopTicketsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(10),
});
export type TopTicketsQuery = z.infer<typeof TopTicketsQuerySchema>;

export const ExportQuerySchema = z.object({
  startDate: z.string().regex(DATE_REGEX).optional(),
  endDate: z.string().regex(DATE_REGEX).optional(),
  type: z.enum(EXPORT_TYPES),
});
export type ExportQuery = z.infer<typeof ExportQuerySchema>;

// ---------------------------------------------------------------------------
// Response schemas — real-time
// ---------------------------------------------------------------------------

export const RealtimeOverviewSchema = z.object({
  currentInPark: z.number().int(),
  todayRevenue: z.number(),
  todayTicketsSold: z.number().int(),
  todayOrders: z.number().int(),
  capacityUtilization: z.number().int(),
  lastUpdated: z.string(),
});
export type RealtimeOverview = z.infer<typeof RealtimeOverviewSchema>;

export const HourlySalesItemSchema = z.object({
  hour: z.number().int().min(0).max(23),
  revenue: z.number(),
  tickets: z.number().int(),
  orders: z.number().int(),
});
export const HourlySalesSchema = z.object({
  hours: z.array(HourlySalesItemSchema),
});
export type HourlySales = z.infer<typeof HourlySalesSchema>;

export const ChannelSalesItemSchema = z.object({
  channel: z.string(),
  revenue: z.number(),
  tickets: z.number().int(),
  orders: z.number().int(),
  percentage: z.number().int(),
});
export const SalesByChannelSchema = z.object({
  channels: z.array(ChannelSalesItemSchema),
});
export type SalesByChannel = z.infer<typeof SalesByChannelSchema>;

export const TopTicketItemSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  sold: z.number().int(),
  revenue: z.number(),
});
export const TopTicketTypesSchema = z.object({
  ticketTypes: z.array(TopTicketItemSchema),
});
export type TopTicketTypes = z.infer<typeof TopTicketTypesSchema>;

// ---------------------------------------------------------------------------
// Response schemas — historical
// ---------------------------------------------------------------------------

export const DailySalesDaySchema = z.object({
  date: z.string().regex(DATE_REGEX),
  revenue: z.number(),
  tickets: z.number().int(),
  orders: z.number().int(),
  avgOrderValue: z.number().int(),
});
export const DailySalesTotalsSchema = z.object({
  revenue: z.number(),
  tickets: z.number().int(),
  orders: z.number().int(),
  avgOrderValue: z.number().int(),
});
export const DailySalesSummarySchema = z.object({
  days: z.array(DailySalesDaySchema),
  totals: DailySalesTotalsSchema,
});
export type DailySalesSummary = z.infer<typeof DailySalesSummarySchema>;

export const MonthlyItemSchema = z.object({
  month: z.number().int().min(1).max(12),
  monthName: z.string(),
  revenue: z.number(),
  tickets: z.number().int(),
  orders: z.number().int(),
});
export const MonthlySalesSchema = z.object({
  months: z.array(MonthlyItemSchema),
});
export type MonthlySales = z.infer<typeof MonthlySalesSchema>;

export const PaymentMethodItemSchema = z.object({
  method: z.string(),
  revenue: z.number(),
  transactions: z.number().int(),
  percentage: z.number().int(),
});
export const RevenueByPaymentMethodSchema = z.object({
  methods: z.array(PaymentMethodItemSchema),
});
export type RevenueByPaymentMethod = z.infer<typeof RevenueByPaymentMethodSchema>;

export const WeeklyItemSchema = z.object({
  weekNumber: z.number().int(),
  startDate: z.string().regex(DATE_REGEX),
  endDate: z.string().regex(DATE_REGEX),
  revenue: z.number(),
  tickets: z.number().int(),
  orders: z.number().int(),
  avgOrderValue: z.number().int(),
  growthPercent: z.number().optional(),
});
export const WeeklyTotalsSchema = z.object({
  revenue: z.number(),
  tickets: z.number().int(),
  orders: z.number().int(),
  avgOrderValue: z.number().int(),
});
export const WeeklySalesSummarySchema = z.object({
  weeks: z.array(WeeklyItemSchema),
  totals: WeeklyTotalsSchema,
});
export type WeeklySalesSummary = z.infer<typeof WeeklySalesSummarySchema>;

export const TicketTypeItemSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  quantity: z.number().int(),
  grossRevenue: z.number(),
  netRevenue: z.number(),
  avgPrice: z.number().int(),
  percentage: z.number().int(),
});
export const TicketTypeTotalsSchema = z.object({
  quantity: z.number().int(),
  grossRevenue: z.number(),
  netRevenue: z.number(),
});
export const RevenueByTicketTypeSchema = z.object({
  ticketTypes: z.array(TicketTypeItemSchema),
  totals: TicketTypeTotalsSchema,
});
export type RevenueByTicketType = z.infer<typeof RevenueByTicketTypeSchema>;

// ---------------------------------------------------------------------------
// Visitor statistics
// ---------------------------------------------------------------------------

export const DailyVisitorTrendItemSchema = z.object({
  date: z.string().regex(DATE_REGEX),
  entries: z.number().int(),
  exits: z.number().int(),
  peakOccupancy: z.number().int(),
  utilizationPercent: z.number().int(),
});
export const HourlyPatternItemSchema = z.object({
  hour: z.number().int().min(0).max(23),
  avgEntries: z.number().int(),
  avgExits: z.number().int(),
});
export const GateStatsItemSchema = z.object({
  gateId: z.string(),
  gateName: z.string(),
  entries: z.number().int(),
  exits: z.number().int(),
});
export const VisitorStatisticsSchema = z.object({
  summary: z.object({
    totalVisitors: z.number().int(),
    avgDailyVisitors: z.number().int(),
    peakDay: z.object({ date: z.string(), visitors: z.number().int() }),
    peakHour: z.object({ hour: z.number().int(), avgVisitors: z.number().int() }),
    capacityUtilization: z.number().int(),
  }),
  dailyTrend: z.array(DailyVisitorTrendItemSchema),
  hourlyPattern: z.array(HourlyPatternItemSchema),
  byGate: z.array(GateStatsItemSchema),
});
export type VisitorStatistics = z.infer<typeof VisitorStatisticsSchema>;

// ---------------------------------------------------------------------------
// Comprehensive report
// ---------------------------------------------------------------------------

export const ComprehensiveSalesReportSchema = z.object({
  period: z.object({
    start: z.string().regex(DATE_REGEX),
    end: z.string().regex(DATE_REGEX),
    days: z.number().int(),
  }),
  summary: z.object({
    totalRevenue: z.number(),
    totalTickets: z.number().int(),
    totalOrders: z.number().int(),
    avgOrderValue: z.number().int(),
    avgDailyRevenue: z.number().int(),
    revenueGrowth: z.number().optional(),
  }),
  byChannel: z.array(z.object({
    channel: z.string(),
    revenue: z.number(),
    percentage: z.number().int(),
  })),
  byTicketType: z.array(z.object({
    type: z.string(),
    revenue: z.number(),
    quantity: z.number().int(),
  })),
  byPaymentMethod: z.array(z.object({
    method: z.string(),
    revenue: z.number(),
    transactions: z.number().int(),
  })),
  trends: z.object({
    dailyRevenue: z.array(z.object({ date: z.string(), revenue: z.number() })),
    weeklyComparison: z.array(z.unknown()),
  }),
});
export type ComprehensiveSalesReport = z.infer<typeof ComprehensiveSalesReportSchema>;

// ---------------------------------------------------------------------------
// Sales dashboard (composite)
// ---------------------------------------------------------------------------

export const SalesDashboardSchema = z.object({
  overview: RealtimeOverviewSchema,
  hourly: HourlySalesSchema,
  channels: SalesByChannelSchema,
  topTickets: TopTicketTypesSchema,
});
export type SalesDashboard = z.infer<typeof SalesDashboardSchema>;

// ---------------------------------------------------------------------------
// Revenue by channel (AnalyticsRevenueController)
// ---------------------------------------------------------------------------

export const RevenueChannelItemSchema = z.object({
  channel: z.string(),
  label: z.string(),
  totalRevenue: z.number(),
  transactionCount: z.number().int(),
  revenuePercent: z.number().int(),
});
export const RevenuePaymentMethodItemSchema = z.object({
  method: z.string(),
  label: z.string(),
  totalRevenue: z.number(),
  transactionCount: z.number().int(),
  revenuePercent: z.number().int(),
});
export const RevenueByChannelQuerySchema = z.object({
  dateFrom: z.string().regex(DATE_REGEX).optional(),
  dateTo: z.string().regex(DATE_REGEX).optional(),
});
export type RevenueByChannelQuery = z.infer<typeof RevenueByChannelQuerySchema>;
export type RevenueChannelItem = z.infer<typeof RevenueChannelItemSchema>;
export const RevenueByChannelResponseSchema = z.object({
  channels: z.array(RevenueChannelItemSchema),
  byPaymentMethod: z.array(RevenuePaymentMethodItemSchema),
  dateFrom: z.string().regex(DATE_REGEX),
  dateTo: z.string().regex(DATE_REGEX),
});
export type RevenueByChannelResponse = z.infer<typeof RevenueByChannelResponseSchema>;
